import { Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CountryCode, CountryStatesGQL, CountryState } from '../../graphql/gql';
import { of, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface CountryStateValue {
  countryState: string | undefined;
  otherState: string | undefined;
}

/**
 * The code the backend stores for "Other State/Province". It is an in-band sentinel and it is not
 * unique: ISO 3166-2 gives Reykjavikurborg the same code, so a country can offer a real state
 * carrying it. Never treat the code on its own as proof of "Other" - compare against
 * `OTHER_OPTION` by identity, and let a real subdivision win any lookup. See BNDDEV-10063.
 */
const OTHER_STATE_CODE = '0';

@Component({
  selector: 'app-country-state-selector',
  imports: [
    AsyncPipe,
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    SelectModule
  ],
  templateUrl: './country-state-selector.component.html',
  styleUrl: './country-state-selector.component.css',
  providers:[
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryStateSelectorComponent),
      multi: true
    }
  ]
})
export class CountryStateSelectorComponent implements ControlValueAccessor, OnChanges, OnDestroy, AfterViewInit {
  @Input() country?: CountryCode = undefined;
  @Input() placeholder = $localize`:country state selector|placeholder:Select a country state or province`;

  selectedCountryState: CountryState | undefined = undefined;
  countryStates: { [key: string]: CountryState[] } = {};
  private pendingValue: CountryStateValue | undefined = undefined;

  otherState: string | undefined = undefined;
  otherStateFieldVisible: boolean = false;
  disabled = false;

  private readonly OTHER_OPTION: CountryState = {
    isoCode: OTHER_STATE_CODE,
    name: $localize`Other State/Province`
  };

  constructor(
    private countryStatesGQL: CountryStatesGQL,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    // After view init, process any pending value that's waiting for country
    if (this.pendingValue && this.country) {
      this.processPendingValue();
    }
  }

  ngOnDestroy(): void {
    // No cleanup needed
  }

  onChanged: (value: CountryStateValue) => void = () => {};
  onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    const countryChange = changes['country'];

    if (!countryChange) {
      return;
    }

    if (this.country && this.pendingValue) {
      // The country caught up with a value written before it was known - resolve it.
      this.processPendingValue();
      return;
    }

    // A different country was picked. The selection belongs to the previous country's list, so it
    // must not survive the switch: keeping it would submit a state code from the wrong country.
    // `firstChange` is excluded because the initial binding carries no user intent and the value
    // may still be on its way in via `writeValue`.
    if (!countryChange.firstChange && countryChange.previousValue !== countryChange.currentValue) {
      this.clearSelection();
    }
  }

  private clearSelection(): void {
    if (!this.selectedCountryState && !this.otherState) {
      // Nothing to clear; stay quiet rather than writing to (and dirtying) the parent form.
      return;
    }

    this.selectedCountryState = undefined;
    this.otherState = undefined;
    this.otherStateFieldVisible = false;

    this.propagateChange();
  }

  private processPendingValue(): void {
    if (!this.pendingValue || !this.country) {
      return;
    }

    const valueToProcess = this.pendingValue;
    this.pendingValue = undefined;

    this.fetchCountryStates().subscribe(states => {
      this.resolveSelection(states, valueToProcess);
      this.cdr.detectChanges();
    });
  }

  // Turns a stored value into a selection. A real subdivision always wins over `OTHER_OPTION`,
  // because the stored code on its own cannot tell Reykjavikurborg from "other" - only the
  // country's own list can. "Other" is the fallback: for the sentinel code, and for free text that
  // no subdivision accounts for.
  private resolveSelection(states: CountryState[], value: CountryStateValue): void {
    const realState = value.countryState
      ? states.find(state => state !== this.OTHER_OPTION && state.isoCode === value.countryState)
      : undefined;

    if (realState) {
      this.selectedCountryState = realState;
      this.otherState = value.otherState;
      // Legacy rows can carry both; keep the free text visible rather than dropping it silently.
      this.otherStateFieldVisible = !!value.otherState;
      return;
    }

    if (value.otherState || value.countryState === OTHER_STATE_CODE) {
      this.selectedCountryState = this.OTHER_OPTION;
      this.otherState = value.otherState;
      this.otherStateFieldVisible = true;
      return;
    }

    this.selectedCountryState = undefined;
    this.otherState = undefined;
    this.otherStateFieldVisible = false;
  }

  writeValue(value: CountryStateValue): void {
    // CLEAN SLATE: Reset everything
    this.selectedCountryState = undefined;
    this.otherState = undefined;
    this.otherStateFieldVisible = false;
    this.pendingValue = undefined;

    if (!value) {
      this.cdr.detectChanges();
      return;
    }

    if (!value.countryState && !value.otherState) {
      this.cdr.detectChanges();
      return;
    }

    // Resolving needs the country's state list, so the value waits here until the country is
    // known. Going through `resolveSelection` rather than matching the code inline is what keeps
    // one rule for every path - the inline match is how a stored "0" used to resolve two ways.
    this.otherState = value.otherState;
    this.pendingValue = { countryState: value.countryState, otherState: value.otherState };

    if (this.country) {
      this.processPendingValue();
    } else {
      // Wait for ngOnChanges or ngAfterViewInit to process it.
      this.cdr.detectChanges();
    }
  }

  registerOnChange(fn: (value: CountryStateValue) => void) {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  setCountryState(countryState: CountryState | undefined) {
    this.selectedCountryState = countryState;
    // By identity, not by code: `OTHER_OPTION` is the one instance this component appends to the
    // options, and a real state of the chosen country may carry the same code.
    this.otherStateFieldVisible = countryState === this.OTHER_OPTION;

    if (!this.otherStateFieldVisible) {
      this.otherState = undefined;
    }

    this.propagateChange();
    this.onTouched();
  }

  onOtherStateChange(value: string) {
    this.otherState = value;
    this.propagateChange();
  }

  private propagateChange() {
    this.onChanged({
      countryState: this.selectedCountryState?.isoCode,
      otherState: this.otherState
    });
  }

  fetchCountryStates(): Observable<CountryState[]> {
    if (!this.country) {
      return of([this.OTHER_OPTION]);
    } else if (this.countryStates[this.country]) {
      return of([...this.countryStates[this.country!], this.OTHER_OPTION]);
    } else {
      return this.countryStatesGQL.fetch({ country: this.country }).pipe(
        map(result => {
          const states: CountryState[] = result.data.countryStates ?? [];
          this.countryStates[this.country!] = states;
          return [...states, this.OTHER_OPTION];
        })
      );
    }
  }
}